"use client";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@workspace/ui/components/dialog";
import { LoadingSpinner } from "@workspace/ui/components/loading-spinner";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useState } from "react";
import { user } from "../api/queries.client";

interface UserFollowersDialogProps {
  userId: string;
  username: string;
  followersCount: number;
  trigger?: React.ReactNode;
}

export function UserFollowersDialog({
  userId,
  username,
  followersCount,
  trigger,
}: UserFollowersDialogProps) {
  const [open, setOpen] = useState(false);

  // Get followers when dialog opens
  const followersQuery = user.listFollowers({ userId });
  const { data: followersData, isLoading, error } = useQuery({
    ...followersQuery,
    enabled: open && Boolean(userId),
  });


  const defaultTrigger = (
    <Button variant="ghost" className="text-sm">
      <span className="font-semibold">{followersCount}</span>
      <span className="ml-1">
        {followersCount === 1 ? "follower" : "followers"}
      </span>
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {followersCount} {followersCount === 1 ? "follower" : "followers"}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-96">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>Error loading followers list</p>
              <p className="text-sm mt-1">{error.message}</p>
            </div>
          ) : !followersData?.followers || followersData.followers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No followers yet
            </div>
          ) : (
            <div className="space-y-2 p-2">
              {followersData.followers.map((follower) => (
                <Link
                  key={follower.userId}
                  href={`/profile/${follower.username}`}
                  className="group flex cursor-pointer items-center space-x-3 rounded-lg p-3 transition-colors hover:bg-gray-50"
                >
                  <div className="shrink-0">
                    <Avatar className="size-12">
                      <AvatarImage
                        src={follower.avatarUrl}
                        alt={follower.username}
                      />
                      <AvatarFallback>
                        {follower.username?.[0]?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-gray-900 transition-colors group-hover:text-blue-600">
                      @{follower.username}
                    </p>
                  </div>
                  <div className="shrink-0">
                    <svg
                      className="h-4 w-4 text-gray-400 transition-colors group-hover:text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}